import React from 'react'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { Button, Form, Input, Modal, Space, Typography } from 'antd'
import {
  RefundFlowStates,
  useRefundFlow
} from 'components/orders/RefundFlowModal/RefundFlowStates'
import styles from './CancelOrder.module.scss'

const MIN_REASON_LENGTH = 10

interface ICancelOrderProps {
  isOpen: boolean
  // Отменяет заказ. При ошибке должен бросить исключение — модалка покажет ответ сервера
  onConfirm: (cancelReason: string) => Promise<void>
  close: () => void
}

interface IFormData {
  reason: string
}

export const CancelOrder: React.FC<ICancelOrderProps> = ({
  isOpen,
  onConfirm,
  close
}) => {
  const [form] = Form.useForm()
  const { step, errorText, runRefund, backToConfirm } = useRefundFlow(isOpen)

  const isLoading = step === 'loading'

  const onClose = () => {
    close()
    form.resetFields()
  }

  const onSubmit = ({ reason }: IFormData) => {
    runRefund(() => onConfirm(reason))
  }

  return (
    <Modal
      centered
      open={isOpen}
      footer={null}
      width='fit-content'
      closable={!isLoading}
      maskClosable={!isLoading}
      keyboard={!isLoading}
      onCancel={isLoading ? undefined : onClose}
    >
      {step === 'confirm' && (
        <div className={styles.wrapper}>
          <ExclamationCircleOutlined className={styles.icon} />
          <div>
            <Typography.Title level={5}>Отказаться от заказа</Typography.Title>
            <Typography.Text>
              Вы действительно хотите отменить весь заказ целиком?
              <br />
              Отмена негативно скажется на статистике вашего магазина.
              <br />
              Дополнительно могут быть применены штрафные санкции.
              <br />
              Если Вы уверены, пожалуйста, подробно опишите причину
            </Typography.Text>
            <Form form={form} name='cancelOrder' onFinish={onSubmit}>
              <Form.Item
                name='reason'
                rules={[
                  {
                    required: true,
                    message: 'Заполните причину отказа'
                  },
                  {
                    whitespace: true,
                    message: 'Заполните причину отказа'
                  },
                  {
                    transform: (value: string) => value?.trim(),
                    min: MIN_REASON_LENGTH,
                    message: `Опишите причину подробнее — не меньше ${MIN_REASON_LENGTH} символов`
                  }
                ]}
                style={{ marginTop: 15 }}
                hasFeedback
              >
                <Input.TextArea
                  placeholder='Опишите причину отказа'
                  rows={3}
                  style={{ resize: 'none' }}
                  styles={{ textarea: { fontFamily: 'SFProText' } }}
                />
              </Form.Item>
              <Space direction='horizontal' size={8}>
                <Button htmlType='button' onClick={onClose}>
                  Закрыть
                </Button>
                <Button danger htmlType='submit' type='primary'>
                  Отменить
                </Button>
              </Space>
            </Form>
          </div>
        </div>
      )}

      <RefundFlowStates
        step={step}
        errorText={errorText}
        loadingText='Отменяем заказ и возвращаем деньги покупателю…'
        successText='Заказ отменён, деньги возвращены покупателю'
        errorTitle='Не удалось отменить заказ'
        onClose={onClose}
        onRetry={backToConfirm}
      />
    </Modal>
  )
}
